import { GoogleMark } from './GoogleMark'

const ROW_LABEL: Record<string, string> = {
  google: 'Google review',
  facebook: 'Facebook review',
}
const PILL_LABEL: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
  manual: 'Manual',
}
// Platform brand colors, deliberately hardcoded — these identify Google and
// Facebook, so they are not tenant tokens and must not be themed.
//
// The foreground is one step darker than each platform's headline blue because
// the pill renders at 12px/600, which WCAG treats as normal text (4.5:1), not
// large. The headline values failed: #1a73e8 on #e8f0fe is 3.93:1 and #1877f2
// on #e7f0fb is 3.68:1 (measured 2026-07-31; Lighthouse flagged the Google one
// on shop.adventureelevated.com). Google Blue 700 is Google's own accessible-on-
// tint step, so this stays an authentic brand color rather than a tinted guess.
// Ratios below are against the paired background in the same line — recompute
// before changing either half.
const PILL_CLASS: Record<string, string> = {
  google: 'bg-[#e8f0fe] text-[#1967d2]', // 4.68:1
  facebook: 'bg-[#e7f0fb] text-[#145dbf]', // 5.45:1
  manual: 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]',
}

export type SourceBadgeProps = {
  source: string
  /** 'row' = Google mark + "Google review" (wall card footer);
   *  'pill' = colored platform pill (feed card footer). */
  variant: 'row' | 'pill'
}

/** Source attribution for a review. Returns null when a `row` source has no
 *  known label (matches the wall's prior behavior of hiding the row). */
export function SourceBadge({ source, variant }: SourceBadgeProps) {
  if (variant === 'row') {
    const label = ROW_LABEL[source]
    if (!label) return null
    return (
      <div className="mt-5 flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
        {source === 'google' && <GoogleMark className="size-5" />}
        <span>{label}</span>
      </div>
    )
  }
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${
        PILL_CLASS[source] ?? PILL_CLASS.manual
      }`}
    >
      {PILL_LABEL[source] ?? source}
    </span>
  )
}
